/**
 * Open houses (BR-112): one dated appointment each (date, from, to, optional attendee limit), no repeat.
 * They are showing appointments of kind `open_house`, so prospects see them on the public listing and
 * can drop by; they do not book a one-on-one slot. Dates and times are entered and shown in the
 * listing's timezone. Each change is saved as soon as it is submitted; removing one confirms first.
 */

import { useState } from 'react';
import { zonedLocalToUtc } from '@resida/shared';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { scheduleAppointment, updateAppointment } from '../../../../lib/leasing.api';
import { LifecycleConfirmDialog } from '../shared';
import type { OpenHouse } from './availability.query';
import {
  DANGER_LINK_BUTTON_CLASS,
  FIELD_CLASS,
  InlineError,
  LINK_BUTTON_CLASS,
  ShowingsCard,
  usePanelReport,
  type PanelReport,
} from './panel-support';
import { TimeSelect } from './TimeSelect';
import {
  formatDateKey,
  formatTimeRange,
  instantToZonedParts,
  timeToMinutes,
} from './time-utils';
import { useAvailabilityAction } from './useAvailabilityAction';

interface OpenHousesPanelProps {
  listingId: string;
  isClosed: boolean;
  openHouses: OpenHouse[];
  timezone: string;
  /** `YYYY-MM-DD` today in the listing's timezone. */
  today: string;
  /** Wording under the title differs a little between the two showings answers. */
  optional?: boolean;
  report: PanelReport;
}

interface FormState {
  editing: OpenHouse | null;
  date: string;
  startTime: string;
  endTime: string;
  limit: string;
}

function describeOpenHouse(house: OpenHouse, timezone: string) {
  const start = instantToZonedParts(house.scheduledFor, timezone);
  const endIso = new Date(new Date(house.scheduledFor).getTime() + house.durationMinutes * 60_000).toISOString();
  const end = instantToZonedParts(endIso, timezone);
  return { date: start.date, startTime: start.time, endTime: end.time };
}

export function OpenHousesPanel({
  listingId,
  isClosed,
  openHouses,
  timezone,
  today,
  optional,
  report,
}: OpenHousesPanelProps) {
  const { run, saving, error, clearError } = useAvailabilityAction(listingId);
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<OpenHouse | null>(null);
  usePanelReport(report, saving, form !== null);

  const openAdd = () => {
    clearError();
    setFormError(null);
    setForm({ editing: null, date: '', startTime: '11:00', endTime: '13:00', limit: '' });
  };

  const openEdit = (house: OpenHouse) => {
    clearError();
    setFormError(null);
    const parts = describeOpenHouse(house, timezone);
    setForm({
      editing: house,
      date: parts.date,
      startTime: parts.startTime,
      endTime: parts.endTime,
      limit: house.maxAttendees ? String(house.maxAttendees) : '',
    });
  };

  const submit = async () => {
    if (!form) return;
    if (!form.date) return setFormError('Choose a date.');
    if (form.date < today) return setFormError('Choose today or a later date.');
    if (timeToMinutes(form.endTime) <= timeToMinutes(form.startTime)) {
      return setFormError('The end time must be after the start time.');
    }
    const limit = form.limit.trim() === '' ? null : Number(form.limit);
    if (limit !== null && (!Number.isInteger(limit) || limit < 1)) {
      return setFormError('The attendee limit must be a whole number, 1 or more.');
    }
    const start = zonedLocalToUtc(form.date, form.startTime, timezone);
    if (start.kind !== 'ok') return setFormError('That start time does not exist on that date. Pick another time.');
    setFormError(null);
    const durationMinutes = timeToMinutes(form.endTime) - timeToMinutes(form.startTime);
    const scheduledFor = start.utc.toISOString();
    const editing = form.editing;
    const ok = await run(
      () =>
        editing
          ? updateAppointment(listingId, editing.id, { scheduledFor, durationMinutes })
          : scheduleAppointment(listingId, { kind: 'open_house', scheduledFor, durationMinutes, maxAttendees: limit }),
      'Failed to save the open house'
    );
    if (ok) setForm(null);
  };

  const remove = async () => {
    if (!confirmRemove) return;
    const target = confirmRemove;
    const ok = await run(
      () => updateAppointment(listingId, target.id, { outcome: 'cancelled' }),
      'Failed to remove the open house'
    );
    if (ok) setConfirmRemove(null);
  };

  return (
    <ShowingsCard
      data-testid="open-houses-panel"
      title="Open houses"
      titleAdornment={optional ? <Badge variant="secondary">Optional</Badge> : undefined}
      description={
        optional
          ? 'Prospects see these times on the listing and can drop by, on top of your weekly hours.'
          : 'Prospects see these times on the listing and can drop by. They do not book a one-on-one slot.'
      }
    >
      {error && !form && !confirmRemove && (
        <div className="mb-3">
          <InlineError message={error} testId="open-houses-error" />
        </div>
      )}

      {openHouses.length === 0 ? (
        <p data-testid="open-houses-empty" className="text-sm text-muted-foreground">
          None added.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {openHouses.map((house) => {
            const parts = describeOpenHouse(house, timezone);
            return (
              <li
                key={house.id}
                data-testid={`open-house-row-${house.id}`}
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2"
              >
                <div className="flex items-center gap-3">
                  <div
                    aria-hidden="true"
                    className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-md border border-border bg-muted/40 text-center leading-tight"
                  >
                    <span className="text-xs font-medium uppercase text-muted-foreground">
                      {formatDateKey(parts.date, { month: 'short' })}
                    </span>
                    <span className="text-base font-semibold text-foreground">{Number(parts.date.slice(8))}</span>
                  </div>
                  <div className="text-sm">
                    <p className="font-semibold text-foreground">{formatDateKey(parts.date, { weekday: 'long' })}</p>
                    <p className="text-muted-foreground">{formatTimeRange(parts.startTime, parts.endTime)}</p>
                    <p className="text-xs text-muted-foreground">
                      {house.maxAttendees ? `Up to ${house.maxAttendees} people` : 'No attendee limit'}
                    </p>
                  </div>
                </div>
                {!isClosed && (
                  <div className="flex gap-1">
                    <button
                      type="button"
                      data-testid={`open-house-edit-${house.id}`}
                      disabled={saving}
                      aria-label={`Edit the open house on ${formatDateKey(parts.date, { month: 'long', day: 'numeric' })}`}
                      onClick={() => openEdit(house)}
                      className={LINK_BUTTON_CLASS}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      data-testid={`open-house-remove-${house.id}`}
                      disabled={saving}
                      aria-label={`Remove the open house on ${formatDateKey(parts.date, { month: 'long', day: 'numeric' })}`}
                      onClick={() => {
                        clearError();
                        setConfirmRemove(house);
                      }}
                      className={DANGER_LINK_BUTTON_CLASS}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!isClosed && !form && (
        <div className={openHouses.length > 0 ? 'mt-3 border-t border-border pt-3' : 'mt-3'}>
          <Button type="button" variant="outline" data-testid="open-house-add-button" disabled={saving} onClick={openAdd}>
            Add an open house
          </Button>
        </div>
      )}

      {form && (
        <form
          data-testid="open-house-form"
          className="mt-4 space-y-3 border-t border-border pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <h4 className="text-sm font-semibold text-foreground">{form.editing ? 'Edit open house' : 'Add an open house'}</h4>
          <div className="flex flex-wrap gap-3">
            <div className="min-w-[10rem] flex-[2]">
              <label htmlFor="open-house-date" className="mb-1 block text-xs font-medium text-foreground">
                Date
              </label>
              <input
                id="open-house-date"
                data-testid="open-house-date"
                type="date"
                min={today}
                value={form.date}
                disabled={saving}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className={FIELD_CLASS}
              />
            </div>
            <TimeSelect
              id="open-house-start"
              label="From"
              value={form.startTime}
              disabled={saving}
              data-testid="open-house-start"
              onChange={(startTime) => setForm({ ...form, startTime })}
            />
            <TimeSelect
              id="open-house-end"
              label="To"
              value={form.endTime}
              disabled={saving}
              data-testid="open-house-end"
              onChange={(endTime) => setForm({ ...form, endTime })}
            />
          </div>
          <div className="max-w-xs">
            <label htmlFor="open-house-limit" className="mb-1 block text-xs font-medium text-foreground">
              Attendee limit <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <input
              id="open-house-limit"
              data-testid="open-house-limit"
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              placeholder="No limit"
              value={form.limit}
              disabled={saving || form.editing !== null}
              onChange={(e) => setForm({ ...form, limit: e.target.value })}
              className={FIELD_CLASS}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {form.editing
                ? 'To change the limit, remove this open house and add it again.'
                : 'Each open house is one date. Add another for each day.'}
            </p>
          </div>
          {(formError ?? error) && <InlineError message={(formError ?? error)!} testId="open-house-error" />}
          <div className="flex gap-2">
            <Button type="submit" data-testid="open-house-save" disabled={saving}>
              {saving ? 'Saving…' : form.editing ? 'Save changes' : 'Add open house'}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="open-house-cancel"
              disabled={saving}
              onClick={() => {
                setForm(null);
                setFormError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      <LifecycleConfirmDialog
        open={confirmRemove !== null}
        onOpenChange={(next) => {
          if (!next) setConfirmRemove(null);
        }}
        title="Remove this open house?"
        description="Prospects will no longer see it on the listing. Anyone who planned to come is told it was cancelled."
        confirmLabel="Remove open house"
        pendingLabel="Removing…"
        onConfirm={() => void remove()}
        isSubmitting={saving}
        destructive
        testIdPrefix="open-house-remove-confirm"
      >
        {error && confirmRemove && <p className="mb-3 text-sm text-destructive" role="alert">{error}</p>}
      </LifecycleConfirmDialog>
    </ShowingsCard>
  );
}
